import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import StudyShell from "@/components/study/StudyShell";
import StudyRunner from "@/components/study/StudyRunner";
import { STUDY } from "@/constants/testIds";

const SECTIONS = [
  { portion: "state", label: "Illinois State" },
  { portion: "national", label: "National Concepts" },
  { portion: "math", label: "Math Drills" },
];

const REVIEW = [
  { mode: "unanswered", label: "Unanswered" },
  { mode: "missed", label: "Missed questions" },
  { mode: "bookmarks", label: "Bookmarked" },
  { mode: "all", label: "All questions" },
];

const ModeBtn = ({ onClick, label }) => (
  <button
    type="button"
    data-testid={STUDY.modeButton}
    onClick={onClick}
    className="rounded-xl border border-[color:rgba(10,27,51,0.15)] bg-white px-4 py-3 text-left text-sm font-semibold text-[var(--ibp-ink)] transition-colors hover:border-[var(--ibp-navy-950)]"
  >
    {label}
  </button>
);

export default function StudyPractice() {
  const [searchParams] = useSearchParams();
  const requestedPortion = searchParams.get("portion");
  const [active, setActive] = useState(() =>
    SECTIONS.some((section) => section.portion === requestedPortion)
      ? { mode: "section", portion: requestedPortion }
      : null
  ); // {mode, portion, label}

  return (
    <StudyShell testId={STUDY.practicePage}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--ibp-ink)]">Practice</h1>
          <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.6)]">
            Pick a section or review mode. One question at a time, with full
            explanations after you answer.
          </p>
        </div>
        {active && (
          <button
            type="button"
            onClick={() => setActive(null)}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold text-[var(--ibp-navy-950)] hover:bg-[color:rgba(10,27,51,0.06)]"
          >
            Change mode
          </button>
        )}
      </div>

      {!active ? (
        <div className="mt-6 space-y-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
              Practice by section
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {SECTIONS.map((s) => (
                <ModeBtn
                  key={s.portion}
                  label={s.label}
                  onClick={() => setActive({ mode: "section", portion: s.portion })}
                />
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
              Focused drills
            </p>
            <button
              type="button"
              data-testid={STUDY.trapDrillButton}
              onClick={() => setActive({ mode: "trap", label: "Trap Drill" })}
              className="mt-3 flex w-full items-start gap-3 rounded-xl border border-[var(--ibp-gold-500)] bg-[color:rgba(214,162,30,0.08)] px-4 py-3.5 text-left transition-colors hover:bg-[color:rgba(214,162,30,0.14)]"
            >
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-[var(--ibp-gold-500)]" />
              <span>
                <span className="block text-sm font-semibold text-[var(--ibp-ink)]">
                  Trap Drill
                </span>
                <span className="mt-0.5 block text-xs text-[color:rgba(11,18,32,0.65)]">
                  Practice the tricky EXCEPT / NOT / &ldquo;which is FALSE&rdquo; questions that catch
                  people on exam day.
                </span>
              </span>
            </button>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
              Review modes
            </p>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {REVIEW.map((r) => (
                <ModeBtn key={r.mode} label={r.label} onClick={() => setActive(r)} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-6">
          <StudyRunner
            mode={active.mode}
            portion={active.portion}
            emptyText="No questions in this set right now."
          />
        </div>
      )}
    </StudyShell>
  );
}
